import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { SIDE_NAV_WIDTH } from '../layout/constants'

/**
 * Scroll-triggered sticky-nav käyttäjäkortille (workshop 20.5.2026 H3 —
 * "ankkurilinkit ja klikuteltavat kortit"). Materialisoituu viewportin
 * yläreunaan vasta kun käyttäjä on vierittänyt heron ohi — ennen sitä
 * piilossa, jotta initial view pysyy puhtaana.
 *
 * Pattern: scroll-triggered sticky (Apple-tuotesivut, Tailwind-docs,
 * Notion-sivut). IntersectionObserver seuraa sentinel-elementtiä — kun
 * sentinel poistuu viewportista, sticky-bar slidaa esiin ylhäältä.
 *
 * Klikkaus: avaa kortin haitarin (jos kiinni) + scrollIntoView smooth-
 * efektillä. Aktiivinen kortti highlightautuu (toinen IntersectionObserver
 * seuraa sisältö-kortteja).
 */

export interface HyppylinkkiKohde {
  /** DOM-id käyttäjäkortin section-wrapperille (esim. "sektio-laitteet"). */
  id: string
  /** Lyhyt label-teksti napissa. */
  label: string
}

interface Props {
  kohdat: HyppylinkkiKohde[]
  /** Sentinel-ref — luodaan parentissa heron alaosaan, viedään tänne.
   * Kun sentinel poistuu viewportista, sticky-bar materialisoituu. */
  sentinelRef: React.RefObject<HTMLElement | null>
}

/* StickyBar:n tausta on full-width (sidenavin oikealta reunalta viewportin
   oikealle reunalle). padding-left: spacing.s saa ensimmäisen napin vasemman
   reunan linjaamaan sisältö-korttien vasemman reunan kanssa (Main.margin-left
   = SIDE_NAV_WIDTH + spacing.s, Content padding-left = 0). padding-right
   peilaa sisältöalueen oikeaa marginaalia. */
const StickyBar = styled.div<{ $nakyy: boolean }>`
  position: fixed;
  top: 0;
  left: ${SIDE_NAV_WIDTH};
  right: 0;
  z-index: 100;
  background: ${({ theme }) => theme.colors.whiteBase};
  border-bottom: 1px solid ${({ theme }) => theme.colors.depthLight2};
  box-shadow: 0 2px 8px hsla(0, 0%, 0%, 0.04);
  padding: ${({ theme }) => theme.spacing.xs}
    ${({ theme }) => theme.spacing.s};
  display: flex;
  align-items: center;
  transform: translateY(${({ $nakyy }) => ($nakyy ? '0' : '-100%')});
  opacity: ${({ $nakyy }) => ($nakyy ? 1 : 0)};
  transition:
    transform 180ms ease-out,
    opacity 180ms ease-out;
  pointer-events: ${({ $nakyy }) => ($nakyy ? 'auto' : 'none')};
`

const Linkit = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  flex-wrap: wrap;
`

const LinkkiNappi = styled.button<{ $aktiivinen: boolean }>`
  background: ${({ theme, $aktiivinen }) =>
    $aktiivinen ? theme.colors.brandBase : 'transparent'};
  color: ${({ theme, $aktiivinen }) =>
    $aktiivinen ? theme.colors.whiteBase : theme.colors.depthDark2};
  border: 1px solid
    ${({ theme, $aktiivinen }) =>
      $aktiivinen ? theme.colors.brandBase : theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.xxs}
    ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.8125rem;
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;

  &:hover {
    background: ${({ theme, $aktiivinen }) =>
      $aktiivinen ? theme.colors.brandBase : theme.colors.highlightLight4};
    border-color: ${({ theme }) => theme.colors.highlightBase};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 2px;
  }
`

/** Klikkaa kortin haitarin auki jos kiinni, sitten scrollaa kortin kohdalle.
 *  Toteutus DOM-pohjaisesti — välttää SektioHaitarin API-muutoksen. */
function avaaJaScrollaa(id: string) {
  const elem = document.getElementById(id)
  if (!elem) return

  // Etsi sektion accordion-toggle-painike. SektioHaitari renderöi
  // ensimmäisen <button>-elementin sektion ylälaidassa, aria-expanded
  // -attribuutilla.
  const toggleBtn = elem.querySelector(
    'button[aria-expanded]',
  ) as HTMLButtonElement | null

  if (toggleBtn && toggleBtn.getAttribute('aria-expanded') === 'false') {
    toggleBtn.click()
  }

  // Anna haitarin avautua ja Reactin re-renderöityä ennen scrollia
  requestAnimationFrame(() => {
    elem.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

export default function KortinHyppylinkit({ kohdat, sentinelRef }: Props) {
  const [naytaSticky, setNaytaSticky] = useState(false)
  const [aktiivinenId, setAktiivinenId] = useState<string | null>(null)
  const aktiivinenRef = useRef<string | null>(null)

  // Sticky-trigger: kun sentinel-elementti (heron alaosa) poistuu viewportista
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const obs = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        setNaytaSticky(!entry.isIntersecting)
      },
      { threshold: 0 },
    )
    obs.observe(sentinel)
    return () => obs.disconnect()
  }, [sentinelRef])

  // Aktiivisen kortin tunnistus: scroll-listener tarkistaa mikä sektio on
  // lähimpänä viewportin yläreunaa (sticky-barin alapuolella).
  useEffect(() => {
    if (!naytaSticky) {
      setAktiivinenId(null)
      aktiivinenRef.current = null
      return
    }
    function paivitaAktiivinen() {
      const ylareunaOffset = 80 // sticky-barin korkeus + pieni puskuri
      let valittu: string | null = null
      let pieninEtaisyys = Infinity
      for (const k of kohdat) {
        const elem = document.getElementById(k.id)
        if (!elem) continue
        const rect = elem.getBoundingClientRect()
        // Sektio on "aktiivinen" jos sen yläreuna on lähimpänä mutta
        // edelleen yläpuolella sticky-barin alareunaa
        const etaisyys = Math.abs(rect.top - ylareunaOffset)
        if (rect.top <= ylareunaOffset + 100 && etaisyys < pieninEtaisyys) {
          pieninEtaisyys = etaisyys
          valittu = k.id
        }
      }
      if (valittu !== aktiivinenRef.current) {
        aktiivinenRef.current = valittu
        setAktiivinenId(valittu)
      }
    }
    paivitaAktiivinen()
    window.addEventListener('scroll', paivitaAktiivinen, { passive: true })
    return () => window.removeEventListener('scroll', paivitaAktiivinen)
  }, [naytaSticky, kohdat])

  return (
    <StickyBar $nakyy={naytaSticky} aria-label="Pikalinkit korttiin">
      <Linkit>
        {kohdat.map(k => (
          <LinkkiNappi
            key={k.id}
            type="button"
            $aktiivinen={aktiivinenId === k.id}
            onClick={() => avaaJaScrollaa(k.id)}
          >
            {k.label}
          </LinkkiNappi>
        ))}
      </Linkit>
    </StickyBar>
  )
}
